// src/lib/attribution.ts
//
// "Where did this visitor come from?" — captured once per session on the first
// page load and carried through to the order, so the admin Traffic tab can tie
// a channel (Instagram, email, a QR code at an event) to real Square revenue.
//
// TWO SIGNALS, in priority order:
//   1. UTM query params  — explicit, reliable, what you tag your own links with.
//   2. document.referrer — automatic, but social/mobile in-app browsers strip it
//      constantly, so anything paid MUST be UTM-tagged or it lands in "direct".
//
// TWO TOUCHES, both persisted:
//   • FIRST touch — the very first source that ever brought this browser here.
//     Never overwritten. Answers "what introduced them to the brand?"
//   • LAST touch  — the source that started the CURRENT session (30-min idle
//     timeout, the GA4 convention). Answers "what drove this purchase?"
//   Orders are attributed on LAST touch (with first touch stamped alongside),
//   matching how every ad platform reports.
//
// PRIVACY: this is deliberately NOT consent-gated, and that is a considered
// call. We store no identifier, build no cross-site profile, and share nothing
// with a third party — it records only which of OUR OWN links you clicked, as
// part of a transaction you initiated. The consent-gated piece is the anonymous
// VISIT PING (see pingVisit below), which is a network call to our own backend
// and therefore held to the same bar as GA. If you'd rather gate the whole
// thing, wrap captureAttribution() in hasTrackingConsent() — you'll lose
// attribution for everyone who declines, which is most of the value.
//
// ONE EXCEPTION: **GPC always wins.** A visitor sending Global Privacy Control
// is making an explicit, machine-readable opt-out, so captureAttribution()
// stores nothing and purges any earlier record. That keeps the invariant in
// src/lib/consent.ts intact at negligible cost — GPC users are a small slice,
// whereas gating on the banner would forfeit most attribution.

import { hasTrackingConsent, isGpcEnabled } from "./consent";

const STORAGE_KEY = "attribution";
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min — GA4's session definition

export interface Touch {
  source: string;
  medium: string;
  campaign: string;
  content: string;
  landingPath: string;
  at: string; // ISO timestamp
}

interface AttributionRecord {
  first: Touch;
  last: Touch;
  lastSeenAt: number; // epoch ms — drives the session-idle check
}

/** Square metadata values cap at 255 chars; keep our own bound well under it
 *  and strip anything that isn't safely loggable. */
const clean = (value: string | null | undefined, max = 64): string =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._\-|/ ]/g, "")
    .slice(0, max);

/**
 * Known referrer hosts → (source, medium). Only hosts we can classify with
 * confidence appear here; everything else becomes ("<hostname>", "referral"),
 * which is still useful and never lies about the channel.
 */
const REFERRER_MAP: { match: RegExp; source: string; medium: string }[] = [
  { match: /(^|\.)instagram\.com$/, source: "instagram", medium: "social" },
  { match: /(^|\.)l\.instagram\.com$/, source: "instagram", medium: "social" },
  { match: /(^|\.)facebook\.com$/, source: "facebook", medium: "social" },
  { match: /(^|\.)m\.facebook\.com$/, source: "facebook", medium: "social" },
  { match: /(^|\.)fb\.me$/, source: "facebook", medium: "social" },
  { match: /(^|\.)tiktok\.com$/, source: "tiktok", medium: "social" },
  { match: /(^|\.)pinterest\.[a-z.]+$/, source: "pinterest", medium: "social" },
  { match: /(^|\.)(x|twitter)\.com$/, source: "twitter", medium: "social" },
  { match: /(^|\.)t\.co$/, source: "twitter", medium: "social" },
  { match: /(^|\.)youtube\.com$/, source: "youtube", medium: "social" },
  { match: /(^|\.)reddit\.com$/, source: "reddit", medium: "social" },
  { match: /(^|\.)linkedin\.com$/, source: "linkedin", medium: "social" },
  { match: /(^|\.)google\.[a-z.]+$/, source: "google", medium: "organic" },
  { match: /(^|\.)bing\.com$/, source: "bing", medium: "organic" },
  { match: /(^|\.)duckduckgo\.com$/, source: "duckduckgo", medium: "organic" },
  { match: /(^|\.)search\.yahoo\.com$/, source: "yahoo", medium: "organic" },
  { match: /(^|\.)ecosia\.org$/, source: "ecosia", medium: "organic" },
];

/** Classify the current landing into a Touch. Exported for tests. */
export const classifyTouch = (
  url: string,
  referrer: string,
  now: Date = new Date(),
): Touch => {
  let params: URLSearchParams;
  let landingPath = "/";
  try {
    const parsed = new URL(url);
    params = parsed.searchParams;
    landingPath = clean(parsed.pathname, 120) || "/";
  } catch {
    params = new URLSearchParams();
  }

  const campaign = clean(params.get("utm_campaign"));
  const content = clean(params.get("utm_content"));
  const base = { campaign, content, landingPath, at: now.toISOString() };

  // 1. Explicit UTM tagging always wins.
  const utmSource = clean(params.get("utm_source"));
  if (utmSource) {
    return {
      ...base,
      source: utmSource,
      medium: clean(params.get("utm_medium")) || "unknown",
    };
  }

  // 2. Fall back to the referrer.
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      // A same-site referrer is internal navigation, not an arrival.
      if (host && host !== window.location.hostname) {
        const known = REFERRER_MAP.find((r) => r.match.test(host));
        if (known)
          return { ...base, source: known.source, medium: known.medium };
        return {
          ...base,
          source: clean(host.replace(/^www\./, "")),
          medium: "referral",
        };
      }
    } catch {
      // Malformed referrer — fall through to direct.
    }
  }

  // 3. Nothing to go on. Note this bucket is inflated by referrer-stripping
  //    in-app browsers, which is exactly why paid links need UTM tags.
  return { ...base, source: "direct", medium: "none" };
};

const read = (): AttributionRecord | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.first?.source || !parsed?.last?.source) return null;
    return parsed as AttributionRecord;
  } catch {
    return null; // corrupt or unavailable — treat as a fresh visitor
  }
};

const write = (record: AttributionRecord): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Private mode / quota. Attribution degrades to "direct"; never throw on a
    // page load over an analytics nicety.
  }
};

/** Drop anything stored. Used when GPC appears after a record already exists. */
const purge = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to purge */
  }
};

/**
 * Record this page load's attribution. Idempotent within a session: the first
 * touch is written once and never changed, and the last touch only rolls over
 * when a NEW session starts (30 min idle) AND we can actually identify a source
 * — so an internal reload never overwrites a real Instagram touch with "direct".
 *
 * @returns whether this load opened a new session (used to gate the visit ping)
 */
export const captureAttribution = (): boolean => {
  // GPC is an explicit, machine-readable opt-out and "GPC always wins" is the
  // invariant in src/lib/consent.ts — so it wins here too, even though the rest
  // of this module is deliberately outside the consent gate (see file header).
  // Purge as well as skip: the signal can appear after a record already exists,
  // and honoring it should not leave earlier data sitting in storage.
  if (isGpcEnabled()) {
    purge();
    return false;
  }

  const touch = classifyTouch(window.location.href, document.referrer);
  const now = Date.now();
  const existing = read();

  if (!existing) {
    write({ first: touch, last: touch, lastSeenAt: now });
    return true;
  }

  const isNewSession = now - existing.lastSeenAt > SESSION_IDLE_MS;
  // An explicitly tagged arrival starts a new attribution even mid-session —
  // clicking a fresh ad is a real new touch regardless of the clock.
  const isTaggedArrival = touch.source !== "direct";

  const next: AttributionRecord = {
    first: existing.first,
    last: isNewSession && isTaggedArrival ? touch : existing.last,
    lastSeenAt: now,
  };
  write(next);
  return isNewSession;
};

/** The attribution to stamp on an order. Null when nothing was ever captured. */
export const getOrderAttribution = (): {
  source: string;
  medium: string;
  campaign: string;
  firstSource: string;
} | null => {
  const record = read();
  if (!record) return null;
  return {
    source: record.last.source,
    medium: record.last.medium,
    campaign: record.last.campaign,
    firstSource: record.first.source,
  };
};

/**
 * Fire the anonymous visit ping so the Traffic tab has a denominator (visits)
 * for its conversion rate, not just orders.
 *
 * CONSENT-GATED — unlike the attribution capture above, this leaves the browser,
 * so it's held to the same bar as GA. Declined/GPC visitors are simply not
 * counted, which means visit totals UNDERCOUNT and conversion rate OVERSTATES.
 * The Traffic tab says so on screen; don't quietly present these as exact.
 *
 * Fire-and-forget: never blocks or surfaces an error to the visitor.
 */
export const pingVisit = (): void => {
  if (!hasTrackingConsent()) return;
  const record = read();
  if (!record) return;

  const body = JSON.stringify({
    source: record.last.source,
    medium: record.last.medium,
    campaign: record.last.campaign,
    landingPath: record.last.landingPath,
  });

  try {
    // keepalive so the ping survives an immediate navigation away.
    void fetch("/.netlify/functions/track-visit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Same bundled-secret gate every browser-callable function uses. A
        // speed bump, not a secret (docs/security-model.md) — but track-visit
        // rejects without it, so omitting this silently 401s every ping.
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body,
      keepalive: true,
    }).catch(() => {
      /* analytics must never surface an error to the visitor */
    });
  } catch {
    /* ignore */
  }
};

/** Capture + (consent permitting) count this visit. Called once from App. */
export const initAttribution = (): void => {
  const isNewSession = captureAttribution();
  // One ping per session, not per route change — otherwise a browsing visitor
  // inflates their own visit count and craters the conversion rate.
  if (isNewSession) pingVisit();
};
